import React, { useEffect } from 'react'
import ScrollContainer from 'react-indiana-drag-scroll'
import { useNavigate } from 'react-router-dom'
import { faStar } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useWorkoutCategoriesQuery } from 'api/queries/workout-categories'
import { useWorkoutActions } from 'store/workout/selectors'
import { useSetPopup } from 'store/config/selectors'
import { pathsObj } from 'lib/constants/paths'
import { filterWorkoutCategoriesByTabletAccess, orderWorkoutCategories } from 'lib/workoutData'
import withIdleTimer from 'lib/HOC/withIdleTimer'
import Main from 'layout/main'
import { Spinner } from 'components/spinner/spinner'
import WorkoutCard from 'components/workouts/workout-card'
import FavoriteCard from 'components/workouts/favorite-card'
import Header from 'components/header/header'
import Button from 'components/button'
import { DifficultyLevel, INRWorkoutTemplate } from 'interfaces/workout'
import { useTabletsToggle } from 'api/queries/tablets-feature-toggle'
import useFavoriteWorkouts from 'lib/hooks/useFavoriteWorkouts'

// Overview of all workouts available
const Workouts = () => {
  const navigate = useNavigate()
  const { resetWorkout, updateWorkoutSession } = useWorkoutActions()
  const { setFreestylePopup, setFreestyleFavoritePreviewPopup } = useSetPopup()

  const { data, loading } = useWorkoutCategoriesQuery()
  const { data: tabletData, loading: tabletLoading } = useTabletsToggle()

  const workoutClick = (workout: INRWorkoutTemplate) => {
    const level = workout?.variants[0]?.difficultyLevel ?? DifficultyLevel.EXPERIENCED
    const workoutId = workout?.id ?? ''
    navigate(pathsObj.workouts.workoutOverview.getWorkoutOverview(workoutId, level))
  }

  const handleGroupWorkoutClick = () => {
    navigate(pathsObj.groupWorkout.workouts) // Update to the actual path for group workout overview
  }

  useEffect(() => {
    resetWorkout()
  }, [resetWorkout])

  const canSeeGroupWorkouts = tabletData?.apiKey?.[0]?.tablet?.[0]?.canSeeClubJoyGroupWorkouts || false
  const canSeeSpecialWorkoutCategories = tabletData?.apiKey?.[0]?.tablet?.[0]?.canSeeSpecialWorkoutCategories === true
  const hasSubscription = tabletData?.apiKey?.[0]?.tablet?.[0]?.hasSubscription || false
  const isLoading = loading || tabletLoading
  const workoutCategories = data
    ? orderWorkoutCategories(
      filterWorkoutCategoriesByTabletAccess(
        data.workoutTemplateCategory,
        canSeeSpecialWorkoutCategories,
      ),
    )
    : []
  const { favorites, initializeDefault, loading: favoritesLoading } = useFavoriteWorkouts()
  const availableTemplates = workoutCategories.flatMap((category) => category.templates)
  const defaultTemplate = availableTemplates[0]
  const availableTemplatesById = new Map(availableTemplates.map((template) => [template.id, template]))
  const visibleFavorites = favorites.filter(
    (favorite) => favorite.kind === 'freestyle' || availableTemplatesById.has(favorite.templateId),
  )

  useEffect(() => {
    if (!hasSubscription || isLoading || favoritesLoading) return
    void initializeDefault(defaultTemplate)
  }, [defaultTemplate, favoritesLoading, hasSubscription, initializeDefault, isLoading])

  const handleFavoriteClick = (favorite: typeof visibleFavorites[number]) => {
    if (favorite.kind === 'template') {
      const template = availableTemplatesById.get(favorite.templateId)
      if (template) workoutClick(template)
      return
    }

    updateWorkoutSession(favorite.workoutSession)
    setFreestyleFavoritePreviewPopup()
  }

  return (
    <Main className="nextround">
      <div>
        <Header />
        <div className="workouts__config">
          <div className="workouts__config__imgWrapper">
            <div className="workouts__config__img" />
          </div>
          <div className="workouts__config__info">
            <h2>Freestyle</h2>
            <h3 className="light subtitle">Set up your workout</h3>
            <Button fullColor customClickEvent={setFreestylePopup}>
              <h3>Create now</h3>
            </Button>
          </div>
        </div>
        {/* Conditionally render group workout button */}
        {canSeeGroupWorkouts && (
          <div className="workouts__config__info">
            <Button fullColor customClickEvent={handleGroupWorkoutClick}>
              <h3>NR Squad</h3>
            </Button>
          </div>
        )}
        {isLoading && <Spinner className="loading" />}
        {!isLoading && hasSubscription && visibleFavorites.length > 0 && (
          <div className="workouts">
            <h3 className="workouts__favoritesTitle">
              <FontAwesomeIcon aria-hidden="true" icon={faStar} />
              My favorites
            </h3>
            <ScrollContainer className="workouts__scroll" vertical={false}>
              {visibleFavorites.map((favorite) => (
                <FavoriteCard
                  customClick={() => handleFavoriteClick(favorite)}
                  favorite={favorite}
                  key={favorite.favoriteId}
                  template={favorite.kind === 'template' ? availableTemplatesById.get(favorite.templateId) : undefined}
                />
              ))}
            </ScrollContainer>
          </div>
        )}
        {!isLoading &&
          hasSubscription &&
          data &&
          workoutCategories.map((category, key: number) => (
            <div className="workouts" key={key}>
              <h3>{category.name}</h3>
              <ScrollContainer className="workouts__scroll" key={key} vertical={false}>
                {category.templates.map((template: INRWorkoutTemplate, index: number) => {
                  const customClick = () => workoutClick(template);
                  const isFavorite = favorites.some(
                    (favorite) => favorite.kind === 'template' && favorite.templateId === template.id,
                  )
                  return (
                    <WorkoutCard
                      template={template}
                      key={index}
                      customClick={customClick}
                      isFavorite={isFavorite}
                    />
                  );
                })}
              </ScrollContainer>
            </div>
          ))}
        {!isLoading &&
        !hasSubscription && (
          <div className="button-link">
            <h4>Please contact NextRound to gain access to more workouts.</h4>
            <Button color="primary">
              <a href="https://www.nextroundboxing.com/neem-contact-op" target="_blank" rel="noopener noreferrer">
                <h3>Go to contact</h3>
              </a>
            </Button>
          </div>
          )
        }
      </div>
    </Main>
  )
}

export default withIdleTimer(Workouts)
